import { useEffect, useState } from 'react';
import { BrandSelect } from '@/components/BrandSelect';
import { useQuery } from '@tanstack/react-query';
import { isValidEmail, isValidPhone, formatPhone } from '@/lib/platform';
import { supabase } from '@/lib/supabase';
import { useActiveLocationId } from '@/lib/locationContext';
import { useReferralProgramme } from '@/lib/useReferralProgramme';
import { referralWindow } from '@/lib/referral-window';
import { isCell } from '@/lib/cell/config';
import { cellGet, cellPost } from '@/lib/cell/api';
import { getCellSession } from '@/lib/cell/cognito';

/**
 * Refer a Pro — THE SAME DOOR AS THE PUBLIC PAGE (Peter, 2026-08-27: "both the
 * Hub and website goes to the same place - creates a zoho lead for the location
 * they are in").
 *
 * Submits to `referral-submit`, which records the referral in
 * public.referrals (terms and payout frozen as promised that day) and creates
 * the lead in the studio's own CRM. The old form here submitted to
 * `referral-form`, whose every call is GoHighLevel — the DEAD stack — so a
 * pro's referral went into a system nobody reads and never touched the record.
 *
 * THE TIER IS NOT SELF-DECLARED. The old form had an "I'm a current IMAGE
 * Studios Pro" checkbox — which anyone can tick to claim the rent credit. Here
 * the tier comes from BEING SIGNED IN: referral-submit resolves the pro from
 * the session token, so it cannot be claimed by asserting it. The checkbox is
 * gone on purpose.
 *
 * The page renders ONLY when the studio's programme is on — the nav link is
 * gated too (Layout), but a bookmark deserves an honest answer, not a form
 * that goes nowhere.
 */

const PROFESSIONS = [
  'Hair Stylist', 'Esthetician', 'Nail Technician', 'Barber', 'Massage Therapist',
  'Lash Artist', 'Makeup Artist', 'Tattoo Artist', 'Acupuncturist',
  'Med Spa Professional', 'Photographer', 'Other',
];

type FormState = {
  source_first: string; source_last: string; source_phone: string; source_email: string;
  ref_first: string; ref_last: string; ref_phone: string; ref_email: string;
  ref_instagram: string; ref_profession: string; ref_workplace: string; ref_why: string;
  agree_terms: boolean; agree_permission: boolean;
  _hp: string;
};

const EMPTY: FormState = {
  source_first: '', source_last: '', source_phone: '', source_email: '',
  ref_first: '', ref_last: '', ref_phone: '', ref_email: '',
  ref_instagram: '', ref_profession: '', ref_workplace: '', ref_why: '',
  agree_terms: false, agree_permission: false, _hp: '',
};

/** The lifecycle, in a pro's words. The keys are the referrals_status_check
 *  vocabulary — from the studio's own published conditions. */
const STATUS_SAID: Record<string, string> = {
  submitted: 'Sent — the studio is on it',
  contacted: "They've been contacted",
  toured: 'They toured the studio',
  lease_signed: 'Lease signed',
  board_approved: 'Approved',
  first_rent: 'Conditions met — your bonus is owed',
  paid: 'Paid',
  expired: 'Expired',
  denied: 'Not eligible',
};

export default function Refer() {
  const programme = useReferralProgramme();
  const { activeLocationId } = useActiveLocationId();
  const [f, setF] = useState<FormState>(EMPTY);
  const bonus = programme.bonus;

  // Prefill from the same sources My Info shows, so two screens can never
  // disagree about who this is. tenants.phone over pros.phone — pros.phone
  // still holds account-creation placeholders (caught on a real screen,
  // 2026-08-09). Prefilled values stay editable; typed values never clobbered.
  const { data: me } = useQuery({
    queryKey: ['refer-me'],
    queryFn: async () => {
      if (isCell()) { // CELL (M28): GET /v1/pro-hub/users/{authUserId}/refer-me — the REAL signed-in person, as auth.getUser() below
        const uid = (await getCellSession())?.userId;
        if (!uid) return null;
        const { data: r } = await cellGet<{ pro: { id: string; first_name: string | null; last_name: string | null; email: string | null; phone: string | null } | null; tenant: { phone: string | null } | null }>(`/v1/pro-hub/users/${uid}/refer-me`);
        if (!r?.pro) return null;
        return { ...r.pro, phone: r.tenant?.phone ?? r.pro.phone ?? null };
      }
      // CELL (M28): the branch above answers on the cell build; below is the Supabase path, unchanged.
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return null;
      const { data: pro } = await supabase
        .from('pros')
        .select('id, first_name, last_name, email, phone')
        .eq('auth_user_id', uid)
        .maybeSingle();
      if (!pro) return null;
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const { data: tenant } = await supabase
        .from('tenants')
        .select('phone')
        .eq('pro_id', pro.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return { ...pro, phone: tenant?.phone ?? pro.phone ?? null };
    },
  });

  useEffect(() => {
    if (!me) return;
    setF((prev) => ({
      ...prev,
      source_first: prev.source_first || (me.first_name ?? ''),
      source_last:  prev.source_last  || (me.last_name ?? ''),
      source_email: prev.source_email || (me.email ?? ''),
      source_phone: prev.source_phone || (me.phone ? formatPhone(me.phone) : ''),
    }));
  }, [me]);

  // YOUR REFERRALS — the pro's own tracking (Peter: "Both pro and owner should
  // have a tracking of referrals"). Filtered by the pro's own id EXPLICITLY,
  // never bare RLS (the useHasSpotlight lesson: an operator viewing the hub
  // must not see someone else's rows counted as theirs).
  const { data: mine, refetch: refetchMine } = useQuery({
    queryKey: ['my-referrals', me?.id],
    enabled: !!me?.id,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/pros/{proId}/referrals
      if (isCell()) return (await cellGet<{ rows: Parameters<typeof MyReferrals>[0]['mine'] }>(`/v1/pro-hub/pros/${me!.id}/referrals`)).data?.rows ?? [];
      const { data } = await supabase
        .from('referrals')
        .select('id, referral_id, referred_first_name, referred_last_name, referred_profession, status, payout_amount, payout_status, submitted_date, expiration_date')
        .eq('referrer_pro_id', me!.id)
        .order('submitted_date', { ascending: false });
      return data ?? [];
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setF(p => ({ ...p, [k]: v }));
    if (errors[k as string]) setErrors(p => ({ ...p, [k as string]: '' }));
  }

  // ALL CONTACT INFO REQUIRED (Peter, 2026-08-27: name, email, phone — both
  // people). The same contract referral-submit enforces, so the browser never
  // demands less than the door.
  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!f.source_first.trim()) e.source_first = 'Required';
    if (!f.source_last.trim()) e.source_last = 'Required';
    if (!isValidPhone(f.source_phone)) e.source_phone = 'A valid phone number is required';
    if (!isValidEmail(f.source_email)) e.source_email = 'A valid email is required';
    if (!f.ref_first.trim()) e.ref_first = 'Required';
    if (!f.ref_last.trim()) e.ref_last = 'Required';
    if (!isValidPhone(f.ref_phone)) e.ref_phone = 'A valid phone number is required';
    if (!isValidEmail(f.ref_email)) e.ref_email = 'A valid email is required';
    if (!f.agree_terms) e.agree_terms = 'Required';
    if (!f.agree_permission) e.agree_permission = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (f._hp) return; // honeypot
    if (!validate()) return;
    setStatus('loading'); setErrMsg('');

    // The flat contract referral-submit reads. The location comes from the
    // pro's own active studio — the programme whose terms they are shown.
    // CELL (M28): POST /v1/pro-hub/locations/{locationId}/referrals — the same body, recorded by
    // public.pro_submit_referral (cell migration 0028) with the function's rules; no CRM lead on staging.
    const invoke = isCell()
      ? (_fn: string, o: { body: Record<string, unknown> }) =>
          cellPost<{ ok?: boolean; reason?: string; message?: string }>(`/v1/pro-hub/locations/${activeLocationId}/referrals`, o.body)
      : (fn: string, o: { body: Record<string, unknown> }) => supabase.functions.invoke<{ ok?: boolean; reason?: string; message?: string }>(fn, o);
    const { data, error } = await invoke(
      'referral-submit',
      {
        body: {
          location_id: activeLocationId,
          source: 'pro_hub',
          referrer_first_name: f.source_first.trim(),
          referrer_last_name: f.source_last.trim(),
          referrer_email: f.source_email.trim().toLowerCase(),
          referrer_phone: f.source_phone.trim(),
          referred_first_name: f.ref_first.trim(),
          referred_last_name: f.ref_last.trim(),
          referred_email: f.ref_email.trim().toLowerCase(),
          referred_phone: f.ref_phone.trim(),
          referred_instagram: f.ref_instagram.trim(),
          referred_profession: f.ref_profession,
          referred_workplace: f.ref_workplace.trim(),
          why_good_fit: f.ref_why.trim(),
          _hp: f._hp,
        },
      },
    ).catch((e: Error) => ({ data: null, error: e }));

    if (error || !data) {
      setStatus('error');
      setErrMsg('We could not submit that just now. Please try again.');
      return;
    }
    if (!data.ok) {
      const said: Record<string, string> = {
        already_known: data.message ?? 'This person is already in our system, so this referral is not eligible.',
        referrals_not_running: 'The referral program is not running right now.',
        referred_needs_full_contact: "We need the pro's name, email and phone number.",
        referrer_needs_full_contact: 'We need your name, email and phone number.',
      };
      setStatus('error');
      setErrMsg(said[data.reason ?? ''] ?? 'We could not submit that just now. Please try again.');
      return;
    }
    setStatus('success');
    refetchMine();
  }

  // ── the honest closed state ──────────────────────────────────────────────
  if (!programme.loading && !programme.enabled) {
    return (
      <div className="card py-10 text-center">
        <h1 className="ph-sec mb-2">Referral program</h1>
        <p className="text-sm text-image-charcoal max-w-sm mx-auto">
          Your studio isn&rsquo;t running a referral program right now. If that
          changes, this page comes back — ask your studio&rsquo;s owner if you&rsquo;re curious.
        </p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="space-y-4">
        <div className="card text-center py-12">
          <div className="size-14 rounded-full bg-image-pink-gradient text-white flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
          <h2 className="font-display text-2xl font-bold text-image-black mb-2">Referral sent</h2>
          <p className="text-sm text-image-charcoal max-w-sm mx-auto">
            The studio will reach out to them, show them around, and keep you
            posted right here.{bonus ? <> When they sign and move in, your <b>{bonus}</b> arrives
            as rent credit.</> : null}
          </p>
          <button onClick={() => { setF(p => ({ ...EMPTY, source_first: p.source_first, source_last: p.source_last, source_email: p.source_email, source_phone: p.source_phone }))  ; setStatus('idle'); }} className="btn-primary mt-6">
            Refer another pro
          </button>
        </div>
        <MyReferrals mine={mine ?? []} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          Great pros know <span className="text-image-pink">great pros.</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Refer a beauty or wellness professional.
          {bonus ? <> When they sign a lease and move into their own suite, you
          earn <b>{bonus}</b> — as rent credit, straight off your rent.</> : null}
        </p>
      </div>

      {bonus ? (
        <div className="rounded-2xl bg-image-black text-white p-5 flex items-center gap-5">
          <div className="font-display text-4xl font-bold text-image-pink whitespace-nowrap">{bonus}</div>
          <div className="text-xs text-white/80 leading-relaxed">
            per referred pro who signs and moves in — paid to you as <b className="text-white">rent credit</b>,
            credited automatically because you&rsquo;re signed in.
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="card space-y-5">
        <Section title="About you">
          <Row2>
            <Field label="First name" required error={errors.source_first}>
              <input className="input" value={f.source_first} onChange={e => set('source_first', e.target.value)} />
            </Field>
            <Field label="Last name" required error={errors.source_last}>
              <input className="input" value={f.source_last} onChange={e => set('source_last', e.target.value)} />
            </Field>
          </Row2>
          <Row2>
            <Field label="Phone" required error={errors.source_phone}>
              <input className="input" inputMode="tel" placeholder="(555) 123-4567" value={f.source_phone} onChange={e => set('source_phone', formatPhone(e.target.value))} />
            </Field>
            <Field label="Email" required error={errors.source_email}>
              <input className="input" type="email" value={f.source_email} onChange={e => set('source_email', e.target.value)} />
            </Field>
          </Row2>
        </Section>

        <Section title="About the pro you're referring">
          <Row2>
            <Field label="First name" required error={errors.ref_first}>
              <input className="input" value={f.ref_first} onChange={e => set('ref_first', e.target.value)} />
            </Field>
            <Field label="Last name" required error={errors.ref_last}>
              <input className="input" value={f.ref_last} onChange={e => set('ref_last', e.target.value)} />
            </Field>
          </Row2>
          <Row2>
            <Field label="Phone" required error={errors.ref_phone}>
              <input className="input" inputMode="tel" placeholder="(555) 123-4567" value={f.ref_phone} onChange={e => set('ref_phone', formatPhone(e.target.value))} />
            </Field>
            <Field label="Email" required error={errors.ref_email}>
              <input className="input" type="email" value={f.ref_email} onChange={e => set('ref_email', e.target.value)} />
            </Field>
          </Row2>
          <Row2>
            <Field label="Instagram">
              <input className="input" placeholder="@handle" value={f.ref_instagram} onChange={e => set('ref_instagram', e.target.value)} />
            </Field>
            <Field label="What they do">
              <BrandSelect value={f.ref_profession} onChange={v => set('ref_profession', v)} ariaLabel="Profession"
                placeholder="Select…" options={PROFESSIONS} />
            </Field>
          </Row2>
          <Field label="Where they work now">
            <input className="input" placeholder="Salon name, or independent" value={f.ref_workplace} onChange={e => set('ref_workplace', e.target.value)} />
          </Field>
          <Field label="Why they'd be a great fit">
            <textarea className="input" rows={3} maxLength={500} placeholder="A sentence is plenty — what makes them right for a suite of their own?" value={f.ref_why} onChange={e => set('ref_why', e.target.value)} />
          </Field>
        </Section>

        <div className="space-y-2 pt-2 border-t border-image-border">
          <CheckRow checked={f.agree_terms} onChange={v => set('agree_terms', v)} error={!!errors.agree_terms}>
            I agree to the referral terms{programme.terms ? <> — <a href="#terms" className="text-image-pink underline">read them</a></> : null} <span className="text-image-pink">*</span>
          </CheckRow>
          <CheckRow checked={f.agree_permission} onChange={v => set('agree_permission', v)} error={!!errors.agree_permission}>
            They know I&rsquo;m referring them and are happy to be contacted <span className="text-image-pink">*</span>
          </CheckRow>
        </div>

        <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={f._hp} onChange={e => set('_hp', e.target.value)} className="absolute -left-[9999px] opacity-0 w-0 h-0" />

        <button type="submit" className="btn-primary w-full !bg-image-pink hover:!bg-image-plum" disabled={status === 'loading'}>
          {status === 'loading' ? 'Submitting…' : 'Submit referral'}
        </button>

        {status === 'error' && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3" role="alert">
            {errMsg}
          </div>
        )}
      </form>

      <MyReferrals mine={mine ?? []} />

      {programme.terms ? (
        <details id="terms" className="card">
          <summary className="text-sm font-bold cursor-pointer">The full referral terms</summary>
          {/* The owner's words, and the same text frozen onto each referral —
              changing them later never rewrites what a referrer was promised. */}
          <pre className="whitespace-pre-wrap font-sans text-sm text-image-charcoal mt-3">{programme.terms}</pre>
        </details>
      ) : null}
    </div>
  );
}

/** The pro's own referrals, each with where it stands. Renders nothing while
 *  the list is empty — an empty tracker above the form is noise. */
function MyReferrals({ mine }: {
  mine: Array<{
    id: string; referral_id: string | null;
    referred_first_name?: string | null; referred_last_name?: string | null;
    referred_profession: string | null; status: string | null;
    payout_amount: number | null; payout_status: string | null;
    submitted_date: string | null; expiration_date: string | null;
  }>;
}) {
  if (!mine.length) return null;
  return (
    <div className="card">
      <h3 className="ph-sec pb-2 mb-3 border-b border-image-border">Your referrals</h3>
      <ul className="divide-y divide-image-border">
        {mine.map((r) => {
          const name = [r.referred_first_name, r.referred_last_name].filter(Boolean).join(' ')
            || r.referral_id || 'Referral';
          const paid = r.status === 'paid' || r.payout_status === 'Paid';
          return (
            <li key={r.id} className="py-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-image-black">{name}</div>
                <div className="text-xs text-image-charcoal mt-0.5">
                  {r.referred_profession ? `${r.referred_profession} · ` : ''}
                  sent {r.submitted_date ?? ''}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-xs font-bold ${paid ? 'text-green-700' : 'text-image-pink'}`}>
                  {STATUS_SAID[r.status ?? ''] ?? 'Sent'}
                </div>
                {r.payout_amount ? (
                  <div className="text-[11px] text-image-charcoal mt-0.5">${Number(r.payout_amount).toLocaleString()} promised</div>
                ) : null}
                {(() => {
                  // THE CLOCK (REF-11) — same window the owner's board shows.
                  const w = referralWindow(r.expiration_date, r.status);
                  return w
                    ? <div className={`text-[11px] mt-0.5 ${w.urgent ? 'text-image-pink font-semibold' : 'text-image-charcoal'}`}>{w.label}</div>
                    : null;
                })()}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---- form bits ---- */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="ph-sec pb-2 mb-3 border-b border-image-border">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row2({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}
function Field({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-1">
        {label} {required && <span className="text-image-pink">*</span>}
      </label>
      {children}
      {error && <div className="text-[11px] text-red-600 mt-1">{error}</div>}
    </div>
  );
}
function CheckRow({
  checked, onChange, error, children,
}: { checked: boolean; onChange: (v: boolean) => void; error: boolean; children: React.ReactNode }) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer select-none ${error ? 'text-red-600' : 'text-image-black'}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="size-4 mt-0.5 accent-image-pink" />
      <span className="text-xs leading-relaxed">{children}</span>
    </label>
  );
}
